import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, Search, Pencil, Trash2, MapPin, Users, ExternalLink } from 'lucide-react';

const statusColor = {
  Draft: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
  Active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  Paused: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  Closed: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

const emptyJob = {
  title: '', department_name: '', employment_type: 'Full Time',
  location_type: 'On-site', location_text: '', status: 'Draft',
  salary_min: '', salary_max: '', salary_currency: 'USD', salary_period: 'Annual',
  description: '', benefits_summary: '', jira_url: '', work_area: '',
  primary_manager_person_id: '', secondary_manager_person_id: '',
  headcount_target: 1, notes: '',
};

export default function JobsTab({ jobs, refData, loading, apiCall, onRefresh }) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [form, setForm] = useState({ ...emptyJob });
  const [saving, setSaving] = useState(false);

  const filtered = jobs.filter(j => {
    if (statusFilter !== 'all' && j.status !== statusFilter) return false;
    if (search && !j.title.toLowerCase().includes(search.toLowerCase()) &&
        !j.department_name?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const openCreate = () => { setEditingJob(null); setForm({ ...emptyJob }); setDialogOpen(true); };
  const openEdit = (job) => {
    setEditingJob(job);
    setForm({
      title: job.title || '', department_name: job.department_name || '',
      employment_type: job.employment_type || 'Full Time',
      location_type: job.location_type || 'On-site', location_text: job.location_text || '',
      status: job.status || 'Draft',
      salary_min: job.salary_min ?? '', salary_max: job.salary_max ?? '',
      salary_currency: job.salary_currency || 'USD', salary_period: job.salary_period || 'Annual',
      description: job.description || '', benefits_summary: job.benefits_summary || '',
      jira_url: job.jira_url || '', work_area: job.work_area || '',
      primary_manager_person_id: job.primary_manager_person_id || '',
      secondary_manager_person_id: job.secondary_manager_person_id || '',
      headcount_target: job.headcount_target ?? 1, notes: job.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const body = {
        ...form,
        salary_min: form.salary_min ? Number(form.salary_min) : null,
        salary_max: form.salary_max ? Number(form.salary_max) : null,
        headcount_target: Number(form.headcount_target) || 1,
      };
      if (editingJob) {
        await apiCall('PUT', `/recruitment/jobs/${editingJob.job_id}`, body);
        toast.success('Job updated');
      } else {
        await apiCall('POST', '/recruitment/jobs', body);
        toast.success('Job created');
      }
      setDialogOpen(false);
      onRefresh();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const handleDelete = async (job) => {
    if (!confirm(`Delete "${job.title}"?`)) return;
    try {
      await apiCall('DELETE', `/recruitment/jobs/${job.job_id}`);
      toast.success('Job deleted');
      onRefresh();
    } catch (e) { toast.error(e.message); }
  };

  const managerName = (pid) => {
    if (!pid || !refData?.members) return '';
    const m = refData.members.find(x => x.person_id === pid);
    return m?.name || '';
  };

  const salaryLabel = (job) => {
    if (!job.salary_min && !job.salary_max) return '--';
    const fmt = (v) => v ? `${(v/1000).toFixed(0)}k` : '';
    return `${job.salary_currency || ''} ${fmt(job.salary_min)}${job.salary_max ? '–' + fmt(job.salary_max) : ''}`;
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div data-testid="jobs-tab">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative max-w-xs flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search jobs..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 h-9" data-testid="jobs-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[130px] h-9" data-testid="jobs-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {(refData?.job_statuses || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={openCreate} size="sm" data-testid="create-job-btn">
          <Plus className="w-4 h-4 mr-1" /> New Job
        </Button>
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[250px]">Job Title</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Salary</TableHead>
              <TableHead className="text-center">Applicants</TableHead>
              <TableHead>Manager</TableHead>
              <TableHead className="w-[80px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} className="text-center py-12 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                {jobs.length === 0 ? 'No jobs yet. Create your first job posting.' : 'No jobs match filters.'}
              </TableCell></TableRow>
            ) : filtered.map(job => (
              <TableRow key={job.job_id} className="cursor-pointer hover:bg-muted/50" onClick={() => openEdit(job)} data-testid={`job-row-${job.job_id}`}>
                <TableCell className="font-medium">{job.title}</TableCell>
                <TableCell className="text-muted-foreground">{job.department_name || '--'}</TableCell>
                <TableCell className="text-sm">{job.employment_type}</TableCell>
                <TableCell className="text-sm">
                  <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {job.location_type}{job.location_text ? ` · ${job.location_text}` : ''}</span>
                </TableCell>
                <TableCell><Badge className={`${statusColor[job.status] || ''} font-normal text-xs`}>{job.status}</Badge></TableCell>
                <TableCell className="text-right text-sm">{salaryLabel(job)}</TableCell>
                <TableCell className="text-center">
                  <Badge variant="secondary" className="font-normal"><Users className="w-3 h-3 mr-1" />{job.applicants_count || 0}</Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{managerName(job.primary_manager_person_id) || '--'}</TableCell>
                <TableCell>
                  <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(job)}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(job)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingJob ? 'Edit Job' : 'New Job'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Title *</Label>
                <Input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Senior Engineer" data-testid="job-title-input" />
              </div>
              <div>
                <Label>Department</Label>
                <Input value={form.department_name} onChange={e => set('department_name', e.target.value)} placeholder="e.g. Engineering" />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={v => set('status', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(refData?.job_statuses || ['Draft','Active','Paused','Closed']).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Employment Type</Label>
                <Select value={form.employment_type} onValueChange={v => set('employment_type', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(refData?.employment_types || []).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Location Type</Label>
                <Select value={form.location_type} onValueChange={v => set('location_type', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(refData?.location_types || []).map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Location</Label>
                <Input value={form.location_text} onChange={e => set('location_text', e.target.value)} placeholder="e.g. London, UK" />
              </div>
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">Compensation</p>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <Label>Min</Label>
                  <Input type="number" value={form.salary_min} onChange={e => set('salary_min', e.target.value)} placeholder="80000" />
                </div>
                <div>
                  <Label>Max</Label>
                  <Input type="number" value={form.salary_max} onChange={e => set('salary_max', e.target.value)} placeholder="120000" />
                </div>
                <div>
                  <Label>Currency</Label>
                  <Input value={form.salary_currency} onChange={e => set('salary_currency', e.target.value)} />
                </div>
                <div>
                  <Label>Period</Label>
                  <Select value={form.salary_period} onValueChange={v => set('salary_period', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Annual">Annual</SelectItem>
                      <SelectItem value="Monthly">Monthly</SelectItem>
                      <SelectItem value="Hourly">Hourly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">Managers</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Primary Manager</Label>
                  <Select value={form.primary_manager_person_id || '_none'} onValueChange={v => set('primary_manager_person_id', v === '_none' ? '' : v)}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">None</SelectItem>
                      {(refData?.members || []).map(m => <SelectItem key={m.person_id} value={m.person_id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Secondary Manager</Label>
                  <Select value={form.secondary_manager_person_id || '_none'} onValueChange={v => set('secondary_manager_person_id', v === '_none' ? '' : v)}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">None</SelectItem>
                      {(refData?.members || []).map(m => <SelectItem key={m.person_id} value={m.person_id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={e => set('description', e.target.value)} rows={5} placeholder="Job description..." />
            </div>
            <div>
              <Label>Benefits</Label>
              <Textarea value={form.benefits_summary} onChange={e => set('benefits_summary', e.target.value)} rows={2} placeholder="Benefits summary..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Jira Link</Label>
                <Input value={form.jira_url} onChange={e => set('jira_url', e.target.value)} placeholder="https://..." />
              </div>
              <div>
                <Label>Headcount Target</Label>
                <Input type="number" value={form.headcount_target} onChange={e => set('headcount_target', e.target.value)} />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-job-btn">
              {saving ? 'Saving...' : editingJob ? 'Save Changes' : 'Create Job'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
